/**
 * /reset — complete the identity-wide password reset using the token
 * delivered by email (or surfaced via dev_token in non-production).
 *
 * TWO MODES, one route:
 *   - reset (default): the generic "Set a new password" page with the
 *     visible "Reset token" field (forgot-password flow).
 *   - invite (`?invite=1&company=…`, the link in an employee invitation):
 *     welcome copy naming the company, the token hidden (it arrives in the
 *     link), and — when the link has expired / was already used / is wrong —
 *     a "Send me a new link" action backed by the public
 *     POST /auth/resend-invite (neutral answer, no account oracle).
 *   - confirm (`&confirm=1` on top of invite): the email already had a
 *     WorkRight login and a company added it — the person confirms the
 *     address by choosing a new password (security review H5). Two-step
 *     methods are never removed: when the login has one, the page then says
 *     to sign in with the new password and that code to finish joining.
 *     "Not you? Decline" turns the company down (token-gated).
 *
 * Migration note: 1:1 port of `frontend/src/app/(auth)/reset/page.tsx`.
 * next/link → <Link>; useSearchParams → Route.useSearch() for the
 * `?email=&token=` hydration. The form keeps server-side validation as
 * the authority — every field error from the backend lands on its
 * matching <FormMessage /> via handleAuthError.
 */

import { useEffect, useState } from "react";
import {
  Link,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { authApi, toApiError } from "@/lib/api";
import { resetSchema, type ResetValues } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";

const FIELDS = ["email", "token", "new_password"] as const;

const resetSearchSchema = z.object({
  email: z.string().optional().catch(undefined),
  token: z.string().optional().catch(undefined),
  // Invite mode. Tolerant of `1` arriving as a number from the URL parser.
  invite: z.union([z.string(), z.number(), z.boolean()]).optional().catch(undefined),
  // Confirm-to-join mode (existing login, a company is waiting).
  confirm: z.union([z.string(), z.number(), z.boolean()]).optional().catch(undefined),
  company: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/reset")({
  validateSearch: resetSearchSchema,
  component: ResetPage,
});

function isInviteFlag(v: unknown): boolean {
  return v === 1 || v === true || v === "1" || v === "true";
}

function ResetPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { email: emailQ, token: tokenQ, company: companyQ } = search;
  const inviteMode = isInviteFlag(search.invite);
  const confirmMode = inviteMode && isInviteFlag(search.confirm);
  const company = companyQ?.trim() || "";
  const [submitting, setSubmitting] = useState(false);
  // Invite mode: the link is dead (server said invalid_token, or the token
  // never made it into the URL).
  const [linkDead, setLinkDead] = useState(inviteMode && !tokenQ);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  // Email isn't set up on the server: nothing could be sent (the answer is
  // the same for every address), so say so instead of "check your inbox".
  const [resendUnavailable, setResendUnavailable] = useState<string | null>(null);
  // Confirm mode, the login has a two-step method: the password is set and
  // joining finishes at the next sign-in with that method.
  const [awaiting, setAwaiting] = useState<{ company: string; support: string } | null>(null);
  // Confirm mode: the person turned the company down.
  const [declined, setDeclined] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);

  const form = useForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: "",
      token: "",
      new_password: "",
      confirm_password: "",
    },
  });

  // Hydrate from the link the user followed (?email=&token=).
  // Run only when the typed params change; setValue is a stable
  // identity but biome/react-hooks/exhaustive-deps prefers the explicit
  // dep.
  useEffect(() => {
    if (emailQ) form.setValue("email", emailQ.toLowerCase());
    if (tokenQ) form.setValue("token", tokenQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailQ, tokenQ]);

  async function onSubmit(values: ResetValues) {
    setSubmitting(true);
    try {
      const res = await authApi.resetPassword({
        email: values.email,
        token: values.token,
        new_password: values.new_password,
      });
      if (res?.awaiting_second_factor) {
        setAwaiting({ company: res.company || company, support: res.support_email ?? "" });
        return;
      }
      const joined = res?.joined_companies ?? [];
      toast.success(
        joined.length > 0
          ? `Email confirmed — sign in to ${joined.join(", ")} with your new password.`
          : inviteMode
            ? "Password set — sign in to get started."
            : "Password updated — sign in with your new password.",
      );
      void navigate({ to: "/login", replace: true });
    } catch (err) {
      if (inviteMode && toApiError(err).error === "invalid_token") {
        // Expired / already used / wrong link: offer a fresh one instead of
        // a toast the person can do nothing with.
        setLinkDead(true);
        setResent(false);
      } else {
        handleAuthError(err, form.setError, FIELDS);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onDecline() {
    setDeclining(true);
    try {
      const res = await authApi.declineSignin({
        email: form.getValues("email").trim(),
        token: form.getValues("token"),
      });
      setDeclined(res.company || company || "this company");
    } catch (err) {
      const e = toApiError(err);
      if (e.error === "invalid_token") {
        setLinkDead(true);
        setResent(false);
      } else {
        toast.error(e.message);
      }
    } finally {
      setDeclining(false);
    }
  }

  async function onResend() {
    const email = form.getValues("email").trim();
    if (!email) {
      form.setError("email", {
        type: "manual",
        message: "Enter your work email to get a new link",
      });
      return;
    }
    setResending(true);
    try {
      const res = await authApi.resendInvite(email);
      setResendUnavailable(res.email_delivery === "unavailable" ? res.message : null);
      setResent(true);
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setResending(false);
    }
  }

  const title = confirmMode
    ? company
      ? `Join ${company}`
      : "Confirm your email"
    : inviteMode
      ? company
        ? `Welcome to ${company}`
        : "Welcome to WorkRight"
      : "Set a new password";
  const description = confirmMode
    ? "This email already has a WorkRight login. Confirm it's yours by choosing a new password."
    : inviteMode
      ? "Choose a password to activate your account. You'll use it to sign in."
      : "Choose a strong password you don't use elsewhere.";

  // After the password is set on a login with a two-step method, or after a
  // decline: one plain message and one next step.
  if (awaiting || declined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">
            {awaiting ? "Password Set" : "Declined"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {awaiting ? (
            <div className="space-y-3 text-sm" data-testid="confirm-awaiting-2fa">
              <p>
                Now sign in with your new password and your usual two-step code
                to finish joining {awaiting.company || "the company"}.
              </p>
              <p className="text-muted-foreground">
                Don&apos;t recognise the two-step method on this login?{" "}
                {awaiting.support ? (
                  <a
                    href={`mailto:${awaiting.support}`}
                    className="inline-flex items-center font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
                  >
                    Contact support
                  </a>
                ) : (
                  "Contact support"
                )}
                .
              </p>
            </div>
          ) : (
            <p className="text-sm" data-testid="confirm-declined">
              {declined} can&apos;t use your login. Nothing else changed.
            </p>
          )}
          <Button asChild className="w-full">
            <Link to="/login" replace>
              Sign In
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {inviteMode && linkDead && (
          <div className="mb-4 space-y-3" data-testid="invite-link-expired">
            {resent && resendUnavailable ? (
              <Alert data-testid="invite-resend-no-email">
                <AlertCircle aria-hidden className="h-4 w-4" />
                <AlertTitle>No new link can be emailed</AlertTitle>
                <AlertDescription>{resendUnavailable}</AlertDescription>
              </Alert>
            ) : resent ? (
              <Alert>
                <CheckCircle2 aria-hidden className="h-4 w-4" />
                <AlertTitle>Check your inbox</AlertTitle>
                <AlertDescription>
                  If that email has an invitation that hasn&apos;t been used
                  yet, we&apos;ve sent a fresh link. It can take a minute to
                  arrive. Already set your password? Just{" "}
                  <Link
                    to="/login"
                    className="inline-flex items-center font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
                  >
                    sign in
                  </Link>{" "}
                  (or use &ldquo;Forgot password&rdquo; there).
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <AlertCircle aria-hidden className="h-4 w-4" />
                <AlertTitle>This invitation link has expired</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>
                    Invitation links work for 7 days and only once. Confirm
                    your email below and we&apos;ll send you a new one.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onResend}
                    disabled={resending}
                    data-testid="invite-resend"
                    className="gap-1.5"
                  >
                    {resending && (
                      <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                    )}
                    Send Me a New Link
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{inviteMode ? "Work Email" : "Email"}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Invite mode: the token rides in the link — never shown. The
                field stays registered (hidden) so the same schema + submit
                path serve both modes. */}
            {!inviteMode && (
              <FormField
                control={form.control}
                name="token"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reset Token</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Paste the token from your email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="new_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {inviteMode ? "Choose a Password" : "New Password"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••••••"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    At least 12 characters, mixing 3 of: lowercase,
                    uppercase, digit, symbol.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirm_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {inviteMode ? "Confirm Password" : "Confirm New Password"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••••••"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={submitting || (inviteMode && linkDead)}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {confirmMode
                ? "Confirm and Set Password"
                : inviteMode
                  ? "Activate My Account"
                  : "Update Password"}
            </Button>
          </form>
        </Form>
        {confirmMode && !linkDead && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-3 w-full"
                disabled={declining || submitting}
                data-testid="confirm-decline"
              >
                Not you? Decline
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Decline {company || "this company"}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {company || "This company"} won&apos;t be able to use your
                  login. Your password and sign-in don&apos;t change.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => void onDecline()}>
                  Decline
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link
            to="/login"
            className="inline-flex items-center font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
          >
            Back to Sign In
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}
